import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useTaskManagement } from '../../hooks/useTaskManagement';
import { usePartnerData } from '../../hooks/usePartnerData';
import { useConnectionData } from '../../hooks/useConnectionData';
import {
  useWebSocket,
  ConnectionStatusIndicator,
} from '../../contexts/WebSocketContext';
import {
  FileActivity,
} from '../../types/dashboard';
// THUB-291: FileProcessingPayload is deliberately NOT imported any more. It declares six
// required fields (fileId, filename, partnerId, taskId, stage, status) that
// broadcast_execution_update does not send, so `data as FileProcessingPayload` was a cast
// TypeScript could not check and produced undefined values in required-string fields.
// handleFileProcessing now reads the frame defensively instead of asserting a shape.
import {
} from '../../types/websocket';
import { DashboardData } from '../../types/analytics';
import { MetricCard } from './MetricCard/MetricCard';
import RecentActivityFeed from './RecentActivityFeed/RecentActivityFeed';
import SystemHealthBanner from './SystemHealthBanner';
import NeedsAttentionPanel, { AttentionItem } from './NeedsAttentionPanel';
import ActivityCharts from './ActivityCharts';
import { fileAuditApi } from '../../services/api/fileAudit';
// THUB-186: timezone-aware shared formatter.
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';

/**
 * Simplified Dashboard Overview
 *
 * Design Philosophy:
 * - An operator glancing at this dashboard should know system health in under 3 seconds
 * - No fake data (trend percentages, change indicators without real historical data)
 * - Clear fraction displays (3/4 healthy) instead of misleading percentages
 * - Industrial/utilitarian aesthetic with high-contrast status indicators
 *
 * Data freshness: Driven by WebSocket real-time updates (see App.tsx subscriptions).
 * Manual Refresh button available for user-initiated reload.
 */
const DashboardOverview: React.FC = () => {
  const navigate = useNavigate();
  const { loading: analyticsLoading, refreshAnalytics } = useAnalytics();
  const { tasks, loading: tasksLoading, dataUpdatedAt: tasksUpdatedAt } = useTaskManagement();
  const { partners, loading: partnersLoading, dataUpdatedAt: partnersUpdatedAt } = usePartnerData();
  const {
    connections,
    loading: connectionsLoading,
    dataUpdatedAt: connectionsUpdatedAt,
  } = useConnectionData();

  const [error, setError] = useState<string | null>(null);
  // THUB-298: "Last: HH:MM" is DERIVED from when data actually arrived, plus the last live event.
  //
  // An effect used to stamp `new Date()` whenever tasks/partners/connections changed identity,
  // which meant the label reported when this component NOTICED the data rather than when the data
  // was fetched -- and it fired on any re-render that produced new array identities, refresh or
  // not. The three hooks now expose their query's `dataUpdatedAt`, so the freshest of those is the
  // truth for fetched data.
  //
  // `lastEventAt` remains state because a WebSocket push or a manual refresh IS an event, not a
  // derivable fact -- and some pushes (handleTaskExecution) deliberately bump the label without
  // changing any of the three lists.
  const [lastEventAt, setLastEventAt] = useState<number>(() => Date.now());
  const lastRefresh = useMemo(
    () => new Date(Math.max(lastEventAt, tasksUpdatedAt || 0, partnersUpdatedAt || 0, connectionsUpdatedAt || 0)),
    [lastEventAt, tasksUpdatedAt, partnersUpdatedAt, connectionsUpdatedAt]
  );
  const setLastRefresh = (_d: Date) => setLastEventAt(Date.now());

  // State for real-time updates via WebSocket
  const [recentActivity, setRecentActivity] = useState<FileActivity[]>([]);

  // Ref for scrolling to attention panel
  const attentionPanelRef = useRef<HTMLDivElement>(null);

  // WebSocket connection status and subscription
  const { isConnected, subscribe } = useWebSocket();

  // Compute dashboard metrics from integrated state
  const dashboardData: DashboardData | null = useMemo(() => {
    if (!tasks.length && !partners.length) return null;

    // Calculate task run statistics
    const taskStats = {
      period: '24h' as const,
      successful: tasks.reduce((sum, t) => sum + (t.metrics?.successfulExecutions || 0), 0),
      failed: tasks.reduce((sum, t) => sum + (t.metrics?.failedExecutions || 0), 0),
      noTransfer: 0,
      total: tasks.reduce((sum, t) => sum + (t.metrics?.totalExecutions || 0), 0),
      trend: 'stable' as const
    };

    // Calculate file activity
    const inboundTasks = tasks.filter(t => t.transferDirection === 'inbound');
    const outboundTasks = tasks.filter(t => t.transferDirection === 'outbound');
    const totalFilesProcessed = tasks.reduce((sum, t) => sum + (t.metrics?.totalFilesProcessed || 0), 0);
    const totalBytesTransferred = tasks.reduce((sum, t) => sum + (t.metrics?.totalBytesTransferred || 0), 0);

    const fileActivity = {
      period: '24h' as const,
      filesReceived: inboundTasks.reduce((sum, t) => sum + (t.metrics?.totalFilesProcessed || 0), 0),
      filesSent: outboundTasks.reduce((sum, t) => sum + (t.metrics?.totalFilesProcessed || 0), 0),
      bytesTransferred: totalBytesTransferred,
      successfulTransfers: tasks.reduce((sum, t) => sum + (t.metrics?.successfulExecutions || 0), 0),
      failedTransfers: tasks.reduce((sum, t) => sum + (t.metrics?.failedExecutions || 0), 0),
      averageFileSize: totalFilesProcessed > 0 ? totalBytesTransferred / totalFilesProcessed : 0
    };

    // Partner summary
    const partnerSummary = {
      totalPartners: partners.length,
      assignedPartners: partners.filter(p => tasks.some(t => t.partnerId === p.partnerId)).length,
      unassignedPartners: partners.filter(p => !tasks.some(t => t.partnerId === p.partnerId)).length,
      activePartners: partners.filter(p => p.isActive).length,
      inactivePartners: partners.filter(p => !p.isActive).length
    };

    // Task summary
    const taskSummary = {
      totalTasks: tasks.length,
      enabledTasks: tasks.filter(t => t.status === 'enabled').length,
      disabledTasks: tasks.filter(t => t.status === 'disabled').length,
      runningTasks: tasks.filter(t => t.lastExecution?.status === 'running').length,
      scheduledTasks: tasks.filter(t => t.schedule?.enabled && t.schedule?.type !== 'manual').length,
      unscheduledTasks: tasks.filter(t => !t.schedule?.enabled || t.schedule?.type === 'manual').length,
      incompleteTasks: tasks.filter(t => t.status === 'disabled' || t.lastExecution?.status === 'failed').length
    };

    // Chart data
    const partnerActivity = partners.map(partner => {
      const partnerTasks = tasks.filter(t => t.partnerId === partner.partnerId);
      const totalExecutions = partnerTasks.reduce((sum, t) => sum + (t.metrics?.totalExecutions || 0), 0);
      return {
        partnerName: partner.name,
        transferCounts: {
          inbound: partnerTasks.filter(t => t.transferDirection === 'inbound').reduce((sum, t) => sum + (t.metrics?.totalExecutions || 0), 0),
          outbound: partnerTasks.filter(t => t.transferDirection === 'outbound').reduce((sum, t) => sum + (t.metrics?.totalExecutions || 0), 0),
        },
        total: totalExecutions
      };
    }).sort((a, b) => b.total - a.total).slice(0, 10);

    const taskActivity = tasks.map(task => {
      const totalExecutions = task.metrics?.totalExecutions || 0;
      const successfulExecutions = task.metrics?.successfulExecutions || 0;
      return {
        taskName: task.name,
        partnerName: task.partnerName,
        transferDirection: task.transferDirection,
        executionCount: totalExecutions,
        successRate: totalExecutions > 0 ? (successfulExecutions / totalExecutions) * 100 : 0
      };
    }).sort((a, b) => b.executionCount - a.executionCount).slice(0, 10);

    return {
      taskStats,
      fileActivity,
      partnerSummary,
      taskSummary,
      chartData: {
        mostActivePartners: partnerActivity,
        mostActiveTasks: taskActivity,
        timeRange: '30d' as const
      }
    };
  }, [tasks, partners]);

  // Helper to detect orphaned tasks (missing or unknown partner/connection)
  const isOrphanedTask = useCallback((task: { partnerName?: string; connectionName?: string }) => {
    const invalidNames = [null, undefined, '', 'Unknown'];
    return invalidNames.includes(task.partnerName as any) || invalidNames.includes(task.connectionName as any);
  }, []);

  // Compute items that need attention
  const attentionItems: AttentionItem[] = useMemo(() => {
    const items: AttentionItem[] = [];

    // Check for orphaned tasks (missing partner or connection)
    const orphanedTasks = tasks.filter(t => isOrphanedTask(t));
    if (orphanedTasks.length > 0) {
      items.push({
        id: 'orphaned-tasks',
        type: 'task',
        name: `${orphanedTasks.length} orphaned task${orphanedTasks.length === 1 ? '' : 's'}`,
        issue: `${orphanedTasks.length} orphaned task${orphanedTasks.length === 1 ? '' : 's'} need${orphanedTasks.length === 1 ? 's' : ''} cleanup`,
        severity: 'warning'
      });
    }

    // Check connections for issues: test failures or provisioning failures only
    connections.forEach(conn => {
      const provStatus = conn.provisioningStatus || conn.status || 'active';
      const hasTestFailure = (conn.connectionTestResults && !conn.connectionTestResults.success);
      const hasProvisioningFailure = provStatus === 'failed';

      if (hasTestFailure || hasProvisioningFailure) {
        let issueMessage = 'Connection has issues';
        if (hasTestFailure && conn.connectionTestResults?.error) {
          issueMessage = conn.connectionTestResults.error;
        } else if (hasTestFailure) {
          issueMessage = 'Connection test failed';
        } else if (hasProvisioningFailure) {
          issueMessage = 'Provisioning failed';
        }

        items.push({
          id: conn.connectionId,
          type: 'connection',
          name: conn.name,
          partnerName: conn.partnerName,
          issue: issueMessage,
          since: conn.lastConnectionTest || conn.updatedAt,
          severity: 'error'
        });
      }
    });

    // Check tasks for execution failures or provisioning failures
    tasks.forEach(task => {
      if (isOrphanedTask(task)) return; // Skip orphaned tasks, handled separately above
      const provStatus = task.provisioningStatus || 'active';
      const hasFailed = task.lastExecution?.status === 'failed';
      const hasProvisioningFailure = provStatus === 'failed';

      if (hasFailed || hasProvisioningFailure) {
        items.push({
          id: task.taskId,
          type: 'task',
          name: task.name,
          partnerName: task.partnerName,
          issue: hasFailed
            ? (task.lastExecution?.errorMessage || 'Task execution failed')
            : 'Provisioning failed',
          since: task.lastExecution?.startTime,
          severity: 'error'
        });
      }
    });

    // Sort by severity (errors first) then by time
    return items.sort((a, b) => {
      if (a.severity !== b.severity) {
        return a.severity === 'error' ? -1 : 1;
      }
      if (a.since && b.since) {
        return new Date(b.since).getTime() - new Date(a.since).getTime();
      }
      return 0;
    });
  }, [connections, tasks, isOrphanedTask]);

  // Compute summary metrics
  const summaryMetrics = useMemo(() => {
    const totalFiles = dashboardData
      ? dashboardData.fileActivity.filesReceived + dashboardData.fileActivity.filesSent
      : 0;

    const activeConnections = connections.filter(c => (c.provisioningStatus || 'active') === 'active');
    const testedPassedConnections = activeConnections.filter(c => c.connectionTestResults && c.connectionTestResults.success).length;

    const enabledTasks = tasks.filter(t => t.status === 'enabled').length;

    // Count tasks with actual failures (not just disabled)
    const failedTasks = tasks.filter(t =>
      t.lastExecution?.status === 'failed' ||
      (t.metrics?.failedExecutions && t.metrics.failedExecutions > 0)
    ).length;

    const activePartners = partners.filter(p => p.isActive).length;

    // Determine status colors - only failures trigger warnings, not disabled tasks
    const connectionStatus: 'success' | 'warning' | 'error' | 'neutral' = testedPassedConnections < activeConnections.length && activeConnections.length > 0 ? 'neutral' : 'success';
    const taskStatus: 'success' | 'warning' | 'error' | 'neutral' = failedTasks > 0 ? 'warning' : 'neutral';

    return {
      transfers: {
        value: totalFiles,
        subtitle: 'files (24h)',
        successCount: dashboardData?.fileActivity.successfulTransfers || 0,
        failCount: dashboardData?.fileActivity.failedTransfers || 0
      },
      connections: {
        value: testedPassedConnections,
        denominator: activeConnections.length,
        subtitle: 'active',
        status: connectionStatus
      },
      tasks: {
        value: enabledTasks,
        denominator: tasks.length,
        subtitle: 'enabled',
        failedCount: failedTasks,
        status: taskStatus
      },
      partners: {
        value: activePartners,
        denominator: partners.length,
        subtitle: 'active',
        status: 'neutral' as const
      }
    };
  }, [dashboardData, connections, tasks, partners]);

  // WebSocket event handler: FILE_PROCESSING
  //
  // THUB-291: this used to be `data as FileProcessingPayload` and then read six fields off it.
  // The cast was a lie TypeScript cannot check, and the wire payload does not match:
  // `broadcast_execution_update` sends { eventType, executionId, taskId, status, progress,
  // timestamp, executionData } -- so fileId, filename, partnerId and stage were ALWAYS
  // undefined. `filename: undefined` then flowed into a field typed `string` (required) and
  // reached `truncateFilename(activity.filename)`, which does `filename.length` -> the
  // "Content Error" TypeError.
  //
  // Reproduced deterministically in DEV: one file dropped on an idle Dashboard, FILE_READY at
  // 22:13:49.985 -> throw at 22:13:50.243 (258ms). It is not intermittent -- it fires on EVERY
  // execution_updates broadcast the Dashboard receives. It only looked random because you have
  // to be sitting on the Dashboard when a file moves, and THUB-264/265 are what made these
  // broadcasts actually arrive.
  //
  // Read the nested `executionData` (where the real per-file detail lives) and fall back to the
  // top level, then DEFAULT every value. A dashboard row is a convenience view: showing
  // "unknown" is correct, blanking the whole page is not.
  const handleFileProcessing = useCallback((data: any) => {
    const raw = (data ?? {}) as Record<string, any>;
    const detail = (raw.executionData ?? {}) as Record<string, any>;
    const pick = (key: string) => detail[key] ?? raw[key];

    const filename = pick('filename');
    // No filename means there is nothing meaningful to show in an activity row, and it is the
    // exact shape that used to crash. Drop the frame rather than render a nameless row.
    if (typeof filename !== 'string' || !filename) {
      setLastRefresh(new Date());
      return;
    }

    const stage = pick('stage');
    const status = pick('status');
    const partnerId = pick('partnerId');
    const taskId = pick('taskId');

    const newActivity: FileActivity = {
      id: pick('fileId') || `${Date.now()}`,
      filename,
      partnerId: partnerId ?? '',
      partnerName: partners.find(p => p.partnerId === partnerId)?.name || 'Unknown Partner',
      taskId: taskId ?? '',
      taskName: tasks.find(t => t.taskId === taskId)?.name || 'Unknown Task',
      eventType: stage === 'archive' ? 'FILE_READY' :
                 stage === 'delivery' ? 'FILE_DELIVERED' :
                 stage === 'pickup' ? 'FILE_PICKED_UP' :
                 stage === 'quarantine' ? 'FILE_FAILED' : 'PROCESSING',
      status: status === 'completed' ? 'success' :
              status === 'failed' ? 'failed' :
              status === 'started' ? 'processing' : 'pending',
      timestamp: new Date().toISOString(),
      errorMessage: pick('errorMessage'),
    };

    // THUB-292: collapse per FILE, not per event. Without this the feed re-fragments the moment
    // files move -- five broadcasts for one file would stack five rows on top of the grouped
    // initial fetch, which is exactly when an operator is looking at it.
    //
    // Replace IN PLACE rather than remove-and-prepend: a file progressing through its stages
    // should update where it sits, not jump to the top on every event. Only a file we have not
    // seen goes to the front. `newActivity.id` is the fileId (see the mapping above), so a row
    // with no fileId falls back to a synthetic id and is treated as its own entry -- it cannot
    // collide with a real file or crash the reducer (THUB-291's payloads are not guaranteed).
    setRecentActivity(prev => {
      const at = prev.findIndex(a => a.id === newActivity.id);
      if (at === -1) return [newActivity, ...prev].slice(0, 50);
      const next = [...prev];
      next[at] = newActivity;   // latest state wins: READY must not keep showing PROCESSING
      return next;
    });
    setLastRefresh(new Date());
  }, [partners, tasks]);

  // WebSocket event handler: TASK_EXECUTION - just update refresh time
  const handleTaskExecution = useCallback((_data: any) => {
    setLastRefresh(new Date());
  }, []);

  // THUB-305: the SYSTEM_ALERT handler, the `systemAlerts` state and the critical-alert banner
  // are DELETED, along with the analytics_updates subscription that fed them.
  //
  // The feature could not fire, for two independent reasons:
  //   1. its publisher (analytics_handler) is never given WEBSOCKET_API_ENDPOINT, so it cannot
  //      deliver -- see infra/cdk/test/broadcaster-env-wiring.test.ts; and
  //   2. THUB-291 established that the analytics_updates payload is
  //      { updateType, timestamp, analyticsData } with no severity/message/title, so the handler
  //      always took its "not an alert shape" branch and could never populate a row anyway.
  //
  // Nothing rendered, because the banner required a CRITICAL alert that never arrived. So this is
  // removing absent capability, not a working feature. If operator-facing alerting is wanted, it
  // needs a publisher that emits severity/message on a topic meant for it -- a feature, not a
  // reinstatement. Git history has the original.

  // THUB-297: hold the handlers in a ref so the subscription effect below does NOT depend on
  // their identity.
  //
  // `handleFileProcessing` closes over `partners`/`tasks`, whose ARRAY IDENTITY changes on every
  // query settle/refetch. Listing it as a dependency meant the effect tore down and re-created
  // all three subscriptions every time that happened -- measured at ~209 cycles in the first
  // 10s of a Dashboard mount on DEV, i.e. 417 needless WebSocket frames per client per mount.
  //
  // Only `analytics_updates` showed on the wire, which is what made this easy to miss:
  // subscribe() in WebSocketContext is reference-counted, and App.tsx independently holds
  // `execution_updates` + `task_status_changes`, so those never dropped to zero subscribers.
  // `analytics_updates` has no other subscriber, so each teardown emitted a real
  // unsubscribe/subscribe pair -- and left a window with ZERO subscribers, during which a
  // broadcast is simply lost.
  //
  // The ref is written after every render, so the handlers still see current partners/tasks;
  // only the SUBSCRIPTION lifetime is decoupled from them.
  const wsHandlersRef = useRef({ handleFileProcessing, handleTaskExecution });
  useEffect(() => {
    wsHandlersRef.current = { handleFileProcessing, handleTaskExecution };
  }, [handleFileProcessing, handleTaskExecution]);

  // Subscribe to WebSocket broadcast topics. Deps are only [isConnected, subscribe]; `subscribe`
  // is stable (sendMessage is useCallback([]), so sendSubscribe/UnsubscribeToServer are too), so
  // this now runs once per connection.
  useEffect(() => {
    if (!isConnected) return;

    const unsubs = [
      subscribe('execution_updates', (data) => wsHandlersRef.current.handleFileProcessing(data)),
      subscribe('task_status_changes', (data) => wsHandlersRef.current.handleTaskExecution(data)),
    ];

    return () => { unsubs.forEach(u => u()); };
  }, [isConnected, subscribe]);

  // Load recent file events on mount
  useEffect(() => {
    const loadRecentEvents = async () => {
      try {
        // THUB-292: groupBy 'file' -> one row per FILE (its latest event), matching Transfer
        // History. Without it a normal inbound run emits five events (LANDED, VALIDATED,
        // PROCESSING, ARCHIVED, READY), so TWO files consumed the whole 10-row feed and the
        // panel read as five near-identical repeats of one filename.
        //
        // This revises THUB-244's decision, which made grouping opt-in and named this surface
        // as legitimately wanting events. Reasonable in principle, but it does not survive a
        // 10-row limit: "Latest file transfer events" is read as "what is moving right now",
        // and ten distinct files answer that far better than two files in ten slots.
        // Task-history and other event-level feeds are deliberately NOT changed.
        const result = await fileAuditApi.searchFileEvents({ limit: 10, groupBy: 'file' });
        if (result.events && result.events.length > 0) {
          const fileActivities: FileActivity[] = result.events.map(event => ({
            // THUB-292: key on fileId, not eventId. The row now represents a FILE, and the
            // merge/dedupe below has to collapse the same file arriving from two sources.
            id: event.fileId || event.eventId || `${Date.now()}-${Math.random()}`,
            filename: event.filename,
            partnerId: event.partnerId,
            partnerName: event.partnerName || 'Unknown Partner',
            taskId: event.taskId,
            taskName: event.taskName || 'Unknown Task',
            eventType: event.eventType as any,
            status: event.status === 'success' || event.status === 'completed' ? 'success' :
                    event.status === 'failed' ? 'failed' :
                    event.status === 'processing' ? 'processing' : 'pending',
            timestamp: event.timestamp,
            errorMessage: event.errorMessage,
          }));
          setRecentActivity(prev => {
            // Keep anything the WebSocket already delivered (it is fresher than this fetch),
            // then append files not already present. Deduping by `id` now means deduping by
            // FILE, which is the point of this change.
            const existingIds = new Set(prev.map(a => a.id));
            const newEvents = fileActivities.filter(a => !existingIds.has(a.id));
            return [...prev, ...newEvents].slice(0, 50);
          });
        }
      } catch (error) {
        console.error('Failed to load recent file events:', error);
      }
    };
    loadRecentEvents();
  }, []); // Only on mount

  const loading = analyticsLoading || tasksLoading || partnersLoading || connectionsLoading;

  const handleRefresh = useCallback(() => {
    setError(null);
    refreshAnalytics();
    setLastRefresh(new Date());
  }, [refreshAnalytics]);

  // Scroll to attention panel
  const handleViewIssues = useCallback(() => {
    attentionPanelRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Handler for activity item click
  //
  // THUB-231 sweep: this was `console.log(...)` only. RecentActivityFeed renders each row
  // as a focusable role="button" with a pointer cursor, so the rows LOOK clickable and did
  // nothing — the same no-op-stub class as the Task "Edit" button this ticket fixed.
  //
  // Navigates to Transfer History rather than deep-linking a pre-filtered search: that page
  // holds its filters in component state and does not read them from the URL, so a
  // `?filename=` link would land on an unfiltered page and quietly look broken. Getting the
  // user to the right screen is honest; faking a prefill is not. Deep-linking is a real
  // improvement but needs FileAuditPage to hydrate from search params first.
  const handleActivityClick = useCallback((_activity: FileActivity) => {
    navigate('/transfer-history');
  }, [navigate]);

  // Handler for attention item click - navigate to the appropriate detail page
  const handleAttentionItemClick = useCallback((item: AttentionItem) => {
    // Navigate to orphaned tasks filter when clicking the orphaned tasks alert
    if (item.id === 'orphaned-tasks') {
      navigate('/tasks?filter=orphaned');
      return;
    }
    switch (item.type) {
      case 'connection':
        navigate('/connections');
        break;
      case 'task':
        navigate('/tasks');
        break;
      case 'partner':
        navigate('/partners');
        break;
    }
  }, [navigate]);

  // Icon components
  const TransfersIcon = (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
    </svg>
  );

  const ConnectionsIcon = (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );

  const TasksIcon = (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  );

  const PartnersIcon = (
    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  // Loading state
  if (loading && !dashboardData) {
    return (
      <div className="p-5 max-w-screen-2xl mx-auto">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="w-10 h-10 border-4 border-neutral-200 border-t-primary-500 rounded-full animate-spin mb-4" />
          <p className="text-neutral-600 dark:text-slate-400 font-body">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !dashboardData) {
    return (
      <div className="p-5 max-w-screen-2xl mx-auto">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="text-center p-6 rounded-xl border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 max-w-md">
            <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
              Error Loading Dashboard
            </h3>
            <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!dashboardData) {
    return (
      <div className="p-5 max-w-screen-2xl mx-auto">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <p className="text-neutral-600 dark:text-slate-400 mb-4">No dashboard data available</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-5 lg:p-6 max-w-screen-2xl mx-auto bg-slate-50 dark:bg-slate-900 min-h-screen">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-mono font-bold text-neutral-900 dark:text-white">
            TransferHub
          </h1>
          <div className="flex items-center gap-3 text-sm text-neutral-500 dark:text-slate-400 mt-1">
            <ConnectionStatusIndicator showText />
            <span className="text-neutral-300 dark:text-slate-600">|</span>
            {/* THUB-186: was toLocaleTimeString([], ...) — browser zone, ignored the selection. */}
            <span>Last: {formatDateTime(lastRefresh, activeTimezone(), 'timeOnly')}</span>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className={`
            inline-flex items-center gap-2 px-4 py-2 font-medium rounded-lg transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
            ${loading
              ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed dark:bg-slate-700 dark:text-slate-500'
              : 'bg-slate-800 hover:bg-slate-700 text-white dark:bg-slate-700 dark:hover:bg-slate-600'
            }
          `}
          aria-label="Refresh dashboard"
        >
          <svg
            className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      {/* System Health Banner - only shows when issues exist */}
      <div className="mb-6">
        <SystemHealthBanner
          issueCount={attentionItems.length}
          onViewIssues={handleViewIssues}
        />
      </div>


      {/* Key Metrics - 4 cards */}
      <section aria-label="Key metrics" className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Transfers (24h) */}
          <MetricCard
            title="Transfers (24h)"
            value={summaryMetrics.transfers.value}
            subtitle={summaryMetrics.transfers.subtitle}
            secondaryText={
              summaryMetrics.transfers.failCount > 0
                ? `${summaryMetrics.transfers.successCount} OK, ${summaryMetrics.transfers.failCount} failed`
                : undefined
            }
            status={summaryMetrics.transfers.failCount > 0 ? 'warning' : 'neutral'}
            icon={TransfersIcon}
            isLoading={loading}
            onClick={() => navigate('/transfer-history')}
          />

          {/* Connections */}
          <MetricCard
            title="Connections"
            value={summaryMetrics.connections.value}
            denominator={summaryMetrics.connections.denominator}
            subtitle={summaryMetrics.connections.subtitle}
            status={summaryMetrics.connections.status}
            icon={ConnectionsIcon}
            isLoading={loading}
            onClick={() => navigate('/connections')}
          />

          {/* Tasks */}
          <MetricCard
            title="Tasks"
            value={summaryMetrics.tasks.value}
            denominator={summaryMetrics.tasks.denominator}
            subtitle={summaryMetrics.tasks.subtitle}
            secondaryText={
              summaryMetrics.tasks.failedCount > 0
                ? `${summaryMetrics.tasks.failedCount} with failures`
                : undefined
            }
            status={summaryMetrics.tasks.status}
            icon={TasksIcon}
            isLoading={loading}
            onClick={() => navigate('/tasks')}
          />

          {/* Partners */}
          <MetricCard
            title="Partners"
            value={summaryMetrics.partners.value}
            denominator={summaryMetrics.partners.denominator}
            subtitle={summaryMetrics.partners.subtitle}
            status={summaryMetrics.partners.status}
            icon={PartnersIcon}
            isLoading={loading}
            onClick={() => navigate('/partners')}
          />
        </div>
      </section>

      {/* Recent Activity + Needs Attention - 2 column */}
      <section aria-label="Activity and attention" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <RecentActivityFeed
          activities={recentActivity}
          maxItems={10}
          isLoading={loading && recentActivity.length === 0}
          onActivityClick={handleActivityClick}
        />
        <div ref={attentionPanelRef}>
          <NeedsAttentionPanel
            items={attentionItems}
            isLoading={loading}
            onItemClick={handleAttentionItemClick}
            maxItems={5}
          />
        </div>
      </section>

      {/* Activity Charts */}
      <section aria-label="Activity charts">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-neutral-200 dark:border-slate-700 overflow-hidden">
          {dashboardData.chartData.mostActivePartners.every(p => p.total === 0) &&
           dashboardData.chartData.mostActiveTasks.every(t => t.executionCount === 0) ? (
            <div className="flex items-center justify-center py-16 text-neutral-500 dark:text-slate-400">
              <p className="text-sm">No transfer data available yet</p>
            </div>
          ) : (
            <ActivityCharts
              data={dashboardData.chartData}
              loading={loading}
            />
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardOverview;
